import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type {
  ImportResult,
  OnImportSubmit,
  ImportSubmitResponse,
} from '../types/template';

export type SubmitOutcome = 'completed' | 'accepted' | 'rejected' | 'stale';

/**
 * Reactive Controller that manages the `onImportSubmit` callback lifecycle.
 *
 * Follows the TemplateLoaderController pattern — AbortController ownership,
 * `isRunning`/`error` state, `reset()` for teardown, and `hostDisconnected()`
 * for DOM cleanup — with one addition: a generation token that allows `reset()`
 * to discard orphaned promise resolutions without aborting the network request
 * (enabling background processing to continue after modal dismissal).
 *
 * Usage:
 *   this._submit = new SubmitController(this, DEFAULT_PROCESSING_DELAY_MS);
 *   const outcome = await this._submit.invoke(importResult, this.onImportSubmit!);
 */
export class SubmitController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _abortController: AbortController | null = null;
  private _delayTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _generation = 0;

  isRunning = false;
  isDelayed = false;
  error: Error | null = null;
  response: ImportSubmitResponse | null = null;

  constructor(
    host: ReactiveControllerHost,
    private _delayMs: number,
  ) {
    this.host = host;
    host.addController(this);
  }

  async invoke(
    result: ImportResult,
    callback: OnImportSubmit,
  ): Promise<SubmitOutcome> {
    // Clear stale state (enables retry from import-error)
    this.error = null;
    this.isDelayed = false;
    this._clearDelayTimer();

    // Abort previous in-flight if any (supersession)
    if (this._abortController) {
      this._abortController.abort();
    }

    // Capture generation token
    this._generation++;
    const gen = this._generation;

    // New AbortController for this invocation
    this._abortController = new AbortController();
    this.isRunning = true;
    this.host.requestUpdate();

    // Start delay timer
    this._delayTimeoutId = setTimeout(() => {
      if (this._generation === gen) {
        this.isDelayed = true;
        this.host.requestUpdate();
      }
    }, this._delayMs);

    try {
      const response: ImportSubmitResponse = await callback(
        result,
        this._abortController.signal,
      );

      // Stale guard
      if (this._generation !== gen) return 'stale';

      this._clearDelayTimer();

      if (response.status === 'accepted') {
        // Async processing: keep isRunning=true, force isDelayed=true
        // so the getter shows 'processing-delay' immediately.
        // After 'accepted', the only escape is _onClose → reset() (user dismisses).
        // NEXT_BLOCKING_VIEWS prevents re-invocation from the UI while isRunning=true.
        this.isDelayed = true;
        this._abortController = null;
        this.host.requestUpdate();
        return 'accepted';
      }

      this.response = response;
      this.isRunning = false;
      this.isDelayed = false;
      this._abortController = null;
      this.host.requestUpdate();
      return 'completed';
    } catch (err) {
      // Stale guard
      if (this._generation !== gen) return 'stale';

      this._clearDelayTimer();
      this.error = err instanceof Error ? err : new Error(String(err));
      this.isRunning = false;
      this.isDelayed = false;
      this._abortController = null;
      this.host.requestUpdate();
      return 'rejected';
    }
  }

  reset(): void {
    // Increment generation — orphaned promise resolution will bail
    this._generation++;
    this._clearDelayTimer();

    // Do NOT abort — host app keeps processing
    this._abortController = null;
    this.isRunning = false;
    this.isDelayed = false;
    this.error = null;
    this.response = null;
    this.host.requestUpdate();
  }

  hostDisconnected(): void {
    this._generation++;
    this._clearDelayTimer();
    this.isRunning = false;
    this.isDelayed = false;

    // DO abort on DOM teardown — honor teardown contract
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  private _clearDelayTimer(): void {
    if (this._delayTimeoutId !== null) {
      clearTimeout(this._delayTimeoutId);
      this._delayTimeoutId = null;
    }
  }
}
